import { queryOptions } from '@tanstack/react-query'

import { api, ensureCsrfCookie, ensureData, publicGet } from '@/shared/api/client'
import type { components } from '@/shared/api/schema'
import type { CurrentAdmin } from '@/shared/auth/permissions'

export type LoginInput = components['schemas']['LoginRequest']

export interface TenantBranding {
  /** Como a marca aparece no menu: ícone com texto, só o ícone ou só a logo. */
  display: 'text' | 'icon' | 'image'
  title: string
  subtitle: string
  icon_url: string | null
  /** Versões para fundo escuro; sem elas vale a imagem clara. */
  icon_dark_url: string | null
  logo_url: string | null
  logo_dark_url: string | null
  /** Cores-base da paleta (#RRGGBB); vazias mantêm a paleta padrão do Ignite. */
  primary_color: string | null
  /** Cor primária própria do tema escuro; vazia deriva da primária. */
  primary_color_dark: string | null
  sidebar_color: string | null
  /** Fundo da página: padrão, cor sólida, degradê (login_color → login_color_to) ou imagem. */
  login_background: 'default' | 'color' | 'gradient' | 'image'
  login_color: string | null
  login_color_to: string | null
  /** Fundo do painel de boas-vindas (card): mesmas opções do fundo da página; a imagem é login_card_url. */
  card_background: 'default' | 'color' | 'gradient' | 'image'
  card_color: string | null
  card_color_to: string | null
  /** Imagem completa no lugar de ícone e texto no topo do formulário. */
  login_logo_url: string | null
  login_logo_dark_url: string | null
  /** Imagem de fundo da página de login (quando `login_background` é `image`). */
  login_background_url: string | null
  /** Imagem do card de boas-vindas (quando `card_background` é `image`). */
  login_card_url: string | null
}

export interface TenantInfo {
  id: string
  name: string
  default_locale: string
  branding: TenantBranding
}

export const authKeys = {
  me: ['auth', 'me'] as const,
  tenant: ['tenant'] as const,
}

export const meQueryOptions = queryOptions({
  queryKey: authKeys.me,
  queryFn: async (): Promise<CurrentAdmin> => {
    return ensureData(await api.GET('/me')).data
  },
  retry: false,
  staleTime: 5 * 60 * 1000,
})

export const tenantQueryOptions = queryOptions({
  queryKey: authKeys.tenant,
  queryFn: async (): Promise<TenantInfo> => (await publicGet<{ data: TenantInfo }>('/tenant')).data,
  staleTime: Infinity,
})

export type LoginResult = { twoFactor: false; admin: CurrentAdmin } | { twoFactor: true }

export type TwoFactorChallengeInput = components['schemas']['TwoFactorChallengeRequest']

/** Com 2FA ativo a senha correta só abre o desafio; o login termina em `submitTwoFactorChallenge`. */
export async function login(input: LoginInput): Promise<LoginResult> {
  await ensureCsrfCookie()

  const result = ensureData(await api.POST('/auth/login', { body: input }))

  if (result.two_factor) return { twoFactor: true }
  if (!result.data) throw new Error('Resposta da API sem corpo.')

  return { twoFactor: false, admin: result.data }
}

export async function submitTwoFactorChallenge(input: TwoFactorChallengeInput): Promise<CurrentAdmin> {
  return ensureData(await api.POST('/auth/two-factor-challenge', { body: input })).data
}

export async function logout(): Promise<void> {
  await api.POST('/auth/logout')
}
